import { useState, useEffect, useCallback } from "react";
import { Delete, Lock } from "lucide-react";

const PASSWORD = "77141714";
const PIN_LENGTH = 8;

export default function LockScreen({ onUnlock }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleDigit = useCallback((d) => {
    setError(false);
    setPin((prev) => (prev.length < PIN_LENGTH ? prev + d : prev));
  }, []);

  const handleDelete = useCallback(() => {
    setError(false);
    setPin((prev) => prev.slice(0, -1));
  }, []);

  useEffect(() => {
    if (pin.length !== PIN_LENGTH) return;
    if (pin === PASSWORD) {
      setSuccess(true);
      const t = setTimeout(() => onUnlock(), 550);
      return () => clearTimeout(t);
    } else {
      setError(true);
      const t = setTimeout(() => { setPin(""); setError(false); }, 700);
      return () => clearTimeout(t);
    }
  }, [pin, onUnlock]);

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) handleDigit(e.key);
      else if (e.key === "Backspace") handleDelete();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleDigit, handleDelete]);

  return (
    <div
      data-testid="lock-screen"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      {/* Animated space background */}
      <div className="stars-bg">
        <div className="stars-layer s1" />
        <div className="stars-layer s2" />
        <div className="stars-layer s3" />
        <div className="shooting-star" />
      </div>

      <div
        className={`lock-card relative z-10 w-full max-w-md p-8 md:p-10 ${error ? "shake" : ""}`}
        data-testid="lock-screen-card"
      >
        <div className="flex flex-col items-center text-center">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/5">
            <Lock className="h-5 w-5 text-white/80" />
          </div>
          <h1 className="font-chivo text-3xl font-black tracking-tighter text-white">
            KrishInvest
          </h1>
          <p className="mt-2 font-mono-jb text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Enter passcode to continue
          </p>

          {/* PIN dots */}
          <div
            className="mt-8 flex items-center justify-center gap-3"
            data-testid="lock-screen-pin-dots"
          >
            {Array.from({ length: PIN_LENGTH }).map((_, i) => (
              <div
                key={i}
                className={`pin-dot ${i < pin.length ? (error ? "error" : "filled") : ""}`}
                data-testid={`pin-dot-${i}`}
              />
            ))}
          </div>

          {/* Keypad */}
          <div
            className="mt-10 grid grid-cols-3 gap-4"
            data-testid="lock-screen-keypad"
          >
            {[1,2,3,4,5,6,7,8,9].map((n) => (
              <button
                key={n}
                type="button"
                className="keypad-key"
                onClick={() => handleDigit(String(n))}
                data-testid={`keypad-digit-${n}`}
                disabled={success}
              >
                {n}
              </button>
            ))}
            <div />
            <button
              type="button"
              className="keypad-key"
              onClick={() => handleDigit("0")}
              data-testid="keypad-digit-0"
              disabled={success}
            >
              0
            </button>
            <button
              type="button"
              className="keypad-key"
              onClick={handleDelete}
              data-testid="keypad-delete"
              disabled={success}
              aria-label="Delete"
            >
              <Delete className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <p
              className="mt-6 font-mono-jb text-xs text-red-400"
              data-testid="lock-screen-error"
            >
              Incorrect passcode. Try again.
            </p>
          )}
          {success && (
            <p
              className="mt-6 font-mono-jb text-xs text-emerald-400"
              data-testid="lock-screen-success"
            >
              Access granted...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
